import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import swal from "sweetalert";
import "react-datepicker/dist/react-datepicker.css";

import {
  getMonthlyTargetAction,
  saveMonthlyTargetAction,
  deleteMonthlyTargetAction,
} from "../../../../store/actions/MonthlyTargetAction.js";

const MonthlyTarget = () => {
  // ================= REDUX SETUP =================
  const dispatch = useDispatch();
  const monthlyTargets = useSelector((state) => state.monthlyTarget?.list || []);

  // ================= LOCAL STATES =================
  const [data, setData] = useState([
    {
      id: "common",
      name: "Common Target",
      leads: "",
      bookings: "",
      conversion: "",
      status: "Active",
    },
  ]);

  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const saveTimeoutRef = useRef(null);
  
  const [searchQueries, setSearchQueries] = useState({
    telecaller: "",
    status: "",
    leads: "",
    bookings: "",
    conversion: "",
  });
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  // ================= FETCH DATA ON MOUNT & MONTH CHANGE =================
  useEffect(() => {
    if (selectedMonth) {
      handleFetchTargets();
    }
  }, [selectedMonth]);

  // ================= FETCH TARGETS FROM API =================
  const handleFetchTargets = async () => {
    try {
      setIsLoading(true);
      setError("");
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();

      console.log(`Fetching targets for: ${month}/${year}`);
      await dispatch(getMonthlyTargetAction(month, year));
      setIsLoading(false);
    } catch (err) {
      console.error("Fetch failed:", err);
      setError("Failed to fetch targets");
      setIsLoading(false);
    }
  };

  // ================= UPDATE DATA FROM REDUX STORE =================
  useEffect(() => {
    console.log("Monthly targets updated:", monthlyTargets);
    
    // Reset data when monthlyTargets changes (including month change)
    if (Array.isArray(monthlyTargets)) {
      const formattedData = [
        {
          id: "common",
          name: "Common Target",
          leads: "",
          bookings: "",
          conversion: "",
          status: "Active",
        },
        ...monthlyTargets
          .filter((target) => target && target.admin_id)
          .map((target) => ({
            id: target.admin_id,
            target_id: target.target_id,
            name: target.admin_name || "Unknown",
            leads: target.monthly_leads_target || "",
            bookings: target.bookings_target || "",
            conversion: target.conversion_target || "",
            status: target.target_status || "Active",
          })),
      ];
      
      setData(formattedData);
    } else {
      // Reset to just common target if no data
      setData([
        {
          id: "common",
          name: "Common Target",
          leads: "",
          bookings: "",
          conversion: "",
          status: "Active",
        },
      ]);
    }
  }, [monthlyTargets]);

  // ================= CLEANUP ON UNMOUNT =================
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // ================= SEARCH HANDLER =================
  const handleSearch = (e, field) => {
    const keyword = e.target.value;
    setSearchQueries((prev) => ({
      ...prev,
      [field]: keyword,
    }));
    setCurrentPage(0);
  };

  // ================= SORT HANDLER =================
  const handleSort = (field) => {
    const order =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0);
  };

  // ================= INPUT CHANGE HANDLER =================
  const handleInputChange = (id, field, value) => {
    // Update state immediately
    setData((prevData) => {
      const updatedData = prevData.map((row) => {
        // Common row → update ALL rows
        if (id === "common") {
          return {
            ...row,
            [field]: value,
          };
        }

        // Normal row update
        if (row.id === id) {
          return {
            ...row,
            [field]: value,
          };
        }

        return row;
      });

      // Trigger save with debounce
      if (value !== "" && (field === "leads" || field === "bookings" || field === "conversion")) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
          triggerAsyncSave(updatedData);
          saveTimeoutRef.current = null;
        }, 800);
      }

      return updatedData;
    });
  };

  // ================= PAGINATION HANDLER =================
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // ================= DELETE HANDLER =================
  const handleDelete = (target) => {
    if (!target.target_id) {
      swal("Error", "Cannot delete unsaved target", "error");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this target!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        setIsDeleting(true);

        try {
          await dispatch(deleteMonthlyTargetAction(target.target_id));
          
          // Refresh data after delete
          const month = selectedMonth.getMonth() + 1;
          const year = selectedMonth.getFullYear();
          await dispatch(getMonthlyTargetAction(month, year));
          
          setSuccess("Target deleted successfully!");
          
          setTimeout(() => setSuccess(""), 2000);
        } catch (error) {
          console.error("Delete failed:", error);
          setError("Failed to delete target");
          setTimeout(() => setError(""), 3000);
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  // ================= TRIGGER ASYNC SAVE =================
  const triggerAsyncSave = async (currentData) => {
    if (isSaving) return;
    
    setIsSaving(true);
    setError("");

    // Filter rows with data
    const validTargets = currentData
      .filter((row) => row.id !== "common")
      .filter((row) => row.leads || row.bookings || row.conversion);

    if (validTargets.length === 0) {
      console.log("No records with data to save");
      setIsSaving(false);
      return;
    }

    // Build payload
    const payload = {
      targets: JSON.stringify(
        validTargets.map((row) => ({
          admin_id: row.id,
          target_month: selectedMonth.getMonth() + 1,
          target_year: selectedMonth.getFullYear(),
          monthly_leads_target: Number(row.leads) || 0,
          bookings_target: Number(row.bookings) || 0,
          conversion_target: Number(row.conversion) || 0,
          target_status: "Active",
        }))
      ),
    };

    console.log("Saving payload:", payload);
    
    try {
      const result = await dispatch(saveMonthlyTargetAction(payload));
      
      if (result?.success) {
        // Refresh data after save
        const month = selectedMonth.getMonth() + 1;
        const year = selectedMonth.getFullYear();
        await dispatch(getMonthlyTargetAction(month, year));
        
        setSuccess("Data saved successfully!");
        setTimeout(() => setSuccess(""), 2000);
      }
    } catch (err) {
      console.error("Save failed:", err);
      setError("Failed to save data");
      setTimeout(() => setError(""), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // ================= APPLY COMMON TARGET TO ALL ROWS =================
  const applyCommonTarget = (field, value) => {
    if (value === "") return;
    
    setData((prevData) => {
      const updatedData = prevData.map((row) => {
        if (row.id === "common") {
          return { ...row, [field]: value };
        }
        return { ...row, [field]: value };
      });
      
      // Trigger save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        triggerAsyncSave(updatedData);
        saveTimeoutRef.current = null;
      }, 800);
      
      return updatedData;
    });
  };

  // ================= FILTER DATA =================
  const filteredData = data.filter((row) => {
    if (row.id === "common") return true;
    
    const matchTelecaller =
      !searchQueries.telecaller ||
      row.name.toLowerCase().includes(searchQueries.telecaller.toLowerCase());
    const matchLeads =
      !searchQueries.leads || String(row.leads).includes(searchQueries.leads);
    const matchBookings =
      !searchQueries.bookings ||
      String(row.bookings).includes(searchQueries.bookings);
    const matchConversion =
      !searchQueries.conversion ||
      String(row.conversion).includes(searchQueries.conversion);

    return matchTelecaller && matchLeads && matchBookings && matchConversion;
  });

  // ================= SORT DATA =================
  const sortedData = [...filteredData].sort((a, b) => {
    if (a.id === "common") return -1;
    if (b.id === "common") return 1;

    let aValue = a[sortField];
    let bValue = b[sortField];

    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // ================= PAGINATE DATA =================
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const paginatedData = sortedData.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  // Format month for display
  const formatMonthYear = (date) => {
    if (!date) return "";
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  return (
    <>
      {/* ===== HEADER SECTION ===== */}
      <div className="row mb-4 align-items-center">
        <div className="col-12">
          <div className="d-flex justify-content-center">
            <div className="position-relative" style={{ width: "250px" }}>
              <DatePicker
                selected={selectedMonth}
                onChange={(date) => {
                  if (date) {
                    setSelectedMonth(date);
                    setCurrentPage(0);
                  }
                }}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                className="form-control text-center"
                placeholderText="Select Month"
              />
              <i
                className="fa fa-calendar position-absolute"
                style={{
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  fontSize: "18px",
                  color: "#6c757d",
                }}
              />
            </div>
          </div>
         
        </div>
      </div>

      {/* ===== STATUS MESSAGES ===== */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fa fa-exclamation-triangle me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fa fa-check-circle me-2"></i>
          {success}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccess("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {isLoading && (
        <div className="alert alert-info" role="alert">
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          Loading monthly targets for {formatMonthYear(selectedMonth)}...
        </div>
      )}

      {/* ===== TABLE SECTION ===== */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead className="table-light">
                <tr>
                  <th className="text-center" style={{ minWidth: "180px" }}>
                    <div onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                      Telecaller Name{" "}
                      {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                    </div>
                    <input
                      className="form-control form-control-sm mt-2"
                      placeholder="Search telecaller"
                      value={searchQueries.telecaller}
                      onChange={(e) => handleSearch(e, "telecaller")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="text-center" style={{ minWidth: "150px" }}>
                    <div onClick={() => handleSort("leads")} style={{ cursor: "pointer" }}>
                      Monthly Leads{" "}
                      {sortField === "leads" && (sortOrder === "asc" ? "↑" : "↓")}
                    </div>
                    <input
                      className="form-control form-control-sm mt-2"
                      placeholder="Search Leads"
                      value={searchQueries.leads}
                      onChange={(e) => handleSearch(e, "leads")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="text-center" style={{ minWidth: "150px" }}>
                    <div onClick={() => handleSort("bookings")} style={{ cursor: "pointer" }}>
                      Bookings{" "}
                      {sortField === "bookings" && (sortOrder === "asc" ? "↑" : "↓")}
                    </div>
                    <input
                      className="form-control form-control-sm mt-2"
                      placeholder="Search Bookings"
                      value={searchQueries.bookings}
                      onChange={(e) => handleSearch(e, "bookings")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="text-center" style={{ minWidth: "150px" }}>
                    <div onClick={() => handleSort("conversion")} style={{ cursor: "pointer" }}>
                      Conversion{" "}
                      {sortField === "conversion" && (sortOrder === "asc" ? "↑" : "↓")}
                    </div>
                    <input
                      className="form-control form-control-sm mt-2"
                      placeholder="Search Conversion"
                      value={searchQueries.conversion}
                      onChange={(e) => handleSearch(e, "conversion")}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                  <th className="text-center" style={{ minWidth: "100px" }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td className="align-middle">
                        {row.id === "common" ? (
                          <strong>{row.name}</strong>
                        ) : (
                          row.name
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Enter leads"
                          value={row.leads}
                          onChange={(e) =>
                            handleInputChange(row.id, "leads", e.target.value)
                          }
                          onBlur={(e) => {
                            if (row.id === "common" && e.target.value) {
                              applyCommonTarget("leads", e.target.value);
                            }
                          }}
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Enter bookings"
                          value={row.bookings}
                          onChange={(e) =>
                            handleInputChange(row.id, "bookings", e.target.value)
                          }
                          onBlur={(e) => {
                            if (row.id === "common" && e.target.value) {
                              applyCommonTarget("bookings", e.target.value);
                            }
                          }}
                          min="0"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Enter conversion"
                          value={row.conversion}
                          onChange={(e) =>
                            handleInputChange(row.id, "conversion", e.target.value)
                          }
                          onBlur={(e) => {
                            if (row.id === "common" && e.target.value) {
                              applyCommonTarget("conversion", e.target.value);
                            }
                          }}
                          min="0"
                          max="100"
                        />
                      </td>
                      <td className="text-center align-middle">
                        {row.id !== "common" && row.target_id && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(row)}
                            disabled={isDeleting}
                            style={{
                              cursor: isDeleting ? "not-allowed" : "pointer",
                              opacity: isDeleting ? 0.5 : 1,
                            }}
                            title="Delete Target"
                          >
                            <i className="fa fa-trash"></i> Delete
                          </button>
                        )}
                        {row.id !== "common" && !row.target_id && (
                          <span className="text-muted">
                            <i className="fa fa-save"></i> Not Saved
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Loading...
                        </>
                      ) : (
                        "No targets found for the selected month"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ===== PAGINATION ===== */}
          {totalPages > 1 && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="text-muted">
                  Showing{" "}
                  {paginatedData.length === 0
                    ? 0
                    : currentPage * rowsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    (currentPage + 1) * rowsPerPage,
                    sortedData.length
                  )}{" "}
                  of {sortedData.length} entries
                </div>
                <div className="d-flex gap-2 flex-wrap justify-content-center">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </button>

                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    let startPage = Math.max(
                      0,
                      currentPage - Math.floor(maxPagesToShow / 2)
                    );
                    let endPage = Math.min(
                      totalPages - 1,
                      startPage + maxPagesToShow - 1
                    );

                    if (endPage - startPage + 1 < maxPagesToShow) {
                      startPage = Math.max(0, endPage - maxPagesToShow + 1);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          className={`btn btn-sm ${currentPage === i ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => handlePageChange(i)}
                        >
                          {i + 1}
                        </button>
                      );
                    }

                    return pages;
                  })()}

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() =>
                      handlePageChange(Math.min(currentPage + 1, totalPages - 1))
                    }
                    disabled={currentPage === totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Saving Indicator */}
      {/* {isSaving && (
        <div className="position-fixed bottom-0 end-0 m-3" style={{ zIndex: 9999 }}>
          <div className="alert alert-info shadow">
            <span className="spinner-border spinner-border-sm me-2"></span>
            Saving changes...
          </div>
        </div>
      )} */}
    </>
  );
};

export default MonthlyTarget;